/**
 * Sign-up hook
 *
 * Provides sign-up functionality with OTP email verification
 */

import { useAuthActions } from "@convex-dev/auth/react";
import { useAction, useMutation } from "convex/react";
import { useCallback, useState } from "react";
import { api } from "convex/_generated/api";
import i18n from "i18next";
import type {
  AuthError,
  AuthStep,
  PasswordRequirement,
  SignUpCredentials,
  SignUpResult,
} from "../types";
import {
  normalizeEmail,
  validateEmail,
  validatePassword,
  getPasswordRequirements,
  sanitizeAuthError,
} from "../utils";
import type { PasswordValidationLabels } from "../utils";

export interface UseSignUpOptions {
  /** Called on successful sign-up (after OTP verification) */
  onSuccess?: () => void;
  /** Called on sign-up error */
  onError?: (error: AuthError) => void;
  /** Custom labels for password requirements (for i18n) */
  passwordLabels?: PasswordValidationLabels;
}

export interface UseSignUpReturn {
  /** Current step in the sign-up flow */
  step: AuthStep;
  /** Sign up with credentials */
  signUp: (credentials: SignUpCredentials) => Promise<SignUpResult>;
  /** Sign up with form data (for form onSubmit handlers) */
  signUpWithForm: (formData: FormData) => Promise<SignUpResult>;
  /** Verify OTP code */
  verifyOtp: (code: string) => Promise<SignUpResult>;
  /** Resend the OTP code */
  resendOtp: () => Promise<void>;
  /** Whether sign-up is in progress */
  isLoading: boolean;
  /** Current error message if any */
  error: string | null;
  /** Current password value (for showing requirements) */
  password: string;
  /** Set password value */
  setPassword: (password: string) => void;
  /** Password requirements with validation status */
  passwordRequirements: PasswordRequirement[];
  /** Whether form has been submitted at least once */
  hasAttemptedSubmit: boolean;
  /** Clear the error */
  clearError: () => void;
  /** Email used for OTP verification (shown in UI) */
  verificationEmail: string | null;
  /** Go back to credentials step */
  backToCredentials: () => void;
}

export function useSignUp(options: UseSignUpOptions = {}): UseSignUpReturn {
  const { signIn: convexSignIn } = useAuthActions();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [step, setStep] = useState<AuthStep>("credentials");
  const [verificationEmail, setVerificationEmail] = useState<string | null>(
    null,
  );
  // Store credentials for resend
  const [storedCredentials, setStoredCredentials] =
    useState<SignUpCredentials | null>(null);

  const checkRateLimit = useAction(api.auth.checkSignUpRateLimit);
  const setPendingLocale = useMutation(api.pending_locale_mutations.set);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const handlePasswordChange = useCallback(
    (newPassword: string) => {
      setPassword(newPassword);
      if (hasAttemptedSubmit) {
        setHasAttemptedSubmit(false);
      }
    },
    [hasAttemptedSubmit],
  );

  const passwordRequirements = getPasswordRequirements(
    password,
    options.passwordLabels,
  );

  const backToCredentials = useCallback(() => {
    setStep("credentials");
    setError(null);
  }, []);

  const signUp = useCallback(
    async (credentials: SignUpCredentials): Promise<SignUpResult> => {
      setIsLoading(true);
      setError(null);
      setHasAttemptedSubmit(true);

      const { email, password: pwd, confirmPassword, name } = credentials;
      const normalizedEmail = normalizeEmail(email);

      // Validate email format
      if (!validateEmail(normalizedEmail)) {
        const authError: AuthError = {
          code: "VALIDATION_INVALID_INPUT",
          message: i18n.t("auth:errors.invalidEmail"),
        };
        setError(authError.message);
        setIsLoading(false);
        options.onError?.(authError);
        return { success: false, error: authError };
      }

      // Validate password requirements
      if (!validatePassword(pwd)) {
        const authError: AuthError = {
          code: "VALIDATION_INVALID_INPUT",
          message: i18n.t("auth:errors.passwordRequirements"),
        };
        setError(authError.message);
        setIsLoading(false);
        options.onError?.(authError);
        return { success: false, error: authError };
      }

      // Validate password confirmation
      if (pwd !== confirmPassword) {
        const authError: AuthError = {
          code: "VALIDATION_INVALID_INPUT",
          message: i18n.t("auth:errors.passwordMismatch"),
        };
        setError(authError.message);
        setIsLoading(false);
        options.onError?.(authError);
        return { success: false, error: authError };
      }

      try {
        await checkRateLimit({});

        // Store user's chosen language so the OTP email is sent in the right language
        await setPendingLocale({
          email: normalizedEmail,
          locale: i18n.language ?? "de",
        });

        const formData = new FormData();
        formData.set("email", normalizedEmail);
        formData.set("password", pwd);
        formData.set("flow", "signUp");
        if (name) {
          formData.set("name", name);
        }

        // With OTP verification enabled, signIn resolves without error
        // but user is NOT yet authenticated — they need to verify their email
        await convexSignIn("password", formData);

        // Store credentials for potential resend, then show OTP step
        setStoredCredentials({ ...credentials, email: normalizedEmail });
        setVerificationEmail(normalizedEmail);
        setStep("otp-verification");
        setError(null);

        return { success: true };
      } catch (err) {
        const rawMessage = err instanceof Error ? err.message : "";
        const errorMessage = sanitizeAuthError(rawMessage, "signUp");
        const authError: AuthError = {
          code: "AUTH_EMAIL_ALREADY_EXISTS",
          message: errorMessage,
        };

        setError(errorMessage);
        options.onError?.(authError);
        return { success: false, error: authError };
      } finally {
        setIsLoading(false);
      }
    },
    [convexSignIn, checkRateLimit, options, setPendingLocale],
  );

  const verifyOtp = useCallback(
    async (code: string): Promise<SignUpResult> => {
      if (!verificationEmail) {
        return {
          success: false,
          error: { code: "NO_EMAIL", message: "No email to verify" },
        };
      }

      setIsLoading(true);
      setError(null);

      try {
        const formData = new FormData();
        formData.set("email", verificationEmail);
        formData.set("code", code);
        formData.set("flow", "email-verification");

        await convexSignIn("password", formData);

        options.onSuccess?.();
        return { success: true };
      } catch (err) {
        const rawMessage = err instanceof Error ? err.message : "";
        const errorMessage = sanitizeAuthError(rawMessage, "otp");
        setError(errorMessage);
        return {
          success: false,
          error: { code: "INVALID_OTP", message: errorMessage },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [convexSignIn, verificationEmail, options],
  );

  const resendOtp = useCallback(async () => {
    if (!storedCredentials) return;

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.set("email", storedCredentials.email);
      formData.set("password", storedCredentials.password);
      formData.set("flow", "signUp");
      if (storedCredentials.name) {
        formData.set("name", storedCredentials.name);
      }

      await convexSignIn("password", formData);
    } catch {
      // Sign-up may error if account already exists, but OTP will be re-sent
    } finally {
      setIsLoading(false);
    }
  }, [convexSignIn, storedCredentials]);

  const signUpWithForm = useCallback(
    async (formData: FormData): Promise<SignUpResult> => {
      const email = formData.get("email") as string;
      const pwd = formData.get("password") as string;
      const confirmPassword = formData.get("confirm-password") as string;
      const name = formData.get("name") as string | undefined;
      return signUp({ email, password: pwd, confirmPassword, name });
    },
    [signUp],
  );

  return {
    step,
    signUp,
    signUpWithForm,
    verifyOtp,
    resendOtp,
    isLoading,
    error,
    password,
    setPassword: handlePasswordChange,
    passwordRequirements,
    hasAttemptedSubmit,
    clearError,
    verificationEmail,
    backToCredentials,
  };
}
